/**
 * THE ONE BODY-SCROLL LOCK (0.1.395).
 *
 * ⚠️ FIXED-BODY, NEVER `overflow: hidden` — the lesson 0.1.368 paid for at the nav band, applied
 * everywhere it was still unlearned. A plain overflow lock is engine-dependent when the page is
 * scrolled: desktop Chrome collapses the scroll offset to 0 (the person loses their place),
 * Android keeps the offset but recomputes `position: sticky` against a viewport that no longer
 * scrolls (the band leaves the screen), and **iOS Safari largely ignores it** — the page behind
 * the sheet keeps rubber-banding and scrolling through. The nav sheet was fixed in 0.1.368; every
 * `<Sheet>` in the app — the acts sheets, the filter sheet, the confirms — was still on the weak
 * technique until this helper, which is why an iOS scroll started under a sheet moved the list.
 *
 * Fixing the body at `-scrollY` freezes the page exactly where it is on every engine, and the
 * recorded offset is restored in one jump on release.
 *
 * ⚠️ REFERENCE-COUNTED, because sheets NEST: a Confirm opens from inside an OptionSheet, so two
 * locks are live and the inner one's release must not unfreeze the page under the outer one. The
 * first lock records the offset and the last release restores it; releases in between only
 * decrement. Each release is idempotent — calling it twice (a double-invoked effect cleanup in
 * StrictMode) must not drop the count below what is actually held.
 */

let held = 0;
let restoreY = 0;
const extras = new Set<string>();

/**
 * Lock the page. Returns the release for this holder — call it exactly once (extra calls are
 * ignored).
 *
 * @param extraClass an additional body class this holder needs while it is locked (the nav sheet
 *   passes `'nav-sheet-lock'`, whose CSS also pins the area band). Removed when this holder
 *   releases, so a nested sheet cannot strip the outer one's class.
 */
export function lockBodyScroll(extraClass?: string): () => void {
  if (typeof document === 'undefined') return () => undefined;

  if (held === 0) {
    restoreY = window.scrollY;
    document.body.style.top = `-${String(restoreY)}px`;
    document.body.classList.add('scroll-lock');
  }
  held += 1;

  // Two holders asking for the same extra class is fine — the class comes off with the last of
  // them, which the Set gives us for free.
  const mine = extraClass !== undefined && !extras.has(extraClass) ? extraClass : null;
  if (mine !== null) {
    extras.add(mine);
    document.body.classList.add(mine);
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (mine !== null) {
      extras.delete(mine);
      document.body.classList.remove(mine);
    }
    held -= 1;
    if (held > 0) return;
    held = 0;
    document.body.classList.remove('scroll-lock');
    document.body.style.top = '';
    window.scrollTo(0, restoreY);
  };
}

/** For tests and the diagnostics screen: how many holders are locking right now. */
export function scrollLockDepth(): number {
  return held;
}
